import { openDB } from 'idb';

interface FileData {
    name: string;
    type: string;
    lastModified: number;
    data: ArrayBuffer;
}

const DB_NAME = 'enquiry-files-db';
const STORE_NAME = 'files';
const DB_VERSION = 1;

export const fileStorage = {
    async init() {
        const db = await openDB(DB_NAME, DB_VERSION, {
            upgrade(db) {
                // Create a store of objects
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME);
                }
            },
        });
        return db;
    },

    async saveFiles(sessionId: string, files: File[]) {
        const fileData = await Promise.all(
            files.map(async (file) => ({
                name: file.name,
                type: file.type,
                lastModified: file.lastModified,
                data: await file.arrayBuffer(),
            })),
        );

        // Then: open the transaction and write to store
        const db = await this.init();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);

        store.put(fileData, sessionId);
        await tx.done;
    },

    async getFiles(sessionId: string): Promise<File[]> {
        const db = await this.init();
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);

        const fileData = await store.get(sessionId);
        if (!fileData) return [];

        // Reconstruct File objects from stored data
        return fileData.map(
            (file: FileData) =>
                new File([file.data], file.name, {
                    type: file.type,
                    lastModified: file.lastModified,
                }),
        );
    },

    /**
     * Drop every stashed file.
     *
     * Sessions are keyed per professional and per gate, so there is no way to
     * enumerate one member's attachments — but on logout every one of them
     * belongs to the member leaving. Their site plans and drawings otherwise
     * outlive the session, the tab and the browser restart, on a device the
     * next person may also use.
     */
    async clearAll() {
        const db = await this.init();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        await tx.objectStore(STORE_NAME).clear();
        await tx.done;
    },

    async clearFiles(sessionId: string) {
        const db = await this.init();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        await store.delete(sessionId);
        await tx.done;
    },
};
